import type { BookedAppointment, BookingPayment, ScreeningAppointmentType, ScreeningProgram } from "./hooks"
import { assign, setup } from "xstate"

// Payment method applies to this booking, not the member.
// Only self-pay is supported; add insurance when its booking path is implemented.
export type BookingPaymentMethod = "self_pay"

// The member's selections on the "Select your preferences" screen — the chosen offering and
// location that key the availability lookup. `offeringId` is the scheduling-api Offering id;
// `region` is a US state code.
export type BookingPreferences = { offeringId: string; region: string }

// The slot the member picked from availability. Mirrors the fields of the scheduling-api
// `Slot` the later booking call needs (the opaque `slotToken` to redeem, and a `hostId`
// chosen from `availableHostIds`); `startTime` is UTC ISO. Carried now so the booking
// ticket doesn't have to reshape the flow.
export type SelectedSlot = {
  slotToken: string
  startTime: string
  offeringId: string
  availableHostIds: string[]
}

export type BookingFlowInput = {
  alreadySignedConsent: boolean
  program: ScreeningProgram
  // Resolved on flow load (initial vs follow-up). Keys the screening question set alongside program.
  appointmentType: ScreeningAppointmentType
  // Known-data eligibility gate (MEM-760): the not-eligible message id from beginBooking, or null
  // when eligible. Non-null routes the flow straight to notEligible at init, before consent, and the
  // id is what the not-eligible page renders (one message defined today).
  knownDataExclusionMessageId: string | null
  // Whether this offering has any member-facing screening questions. False → skip the health-questions
  // step entirely (a follow-up has none), so the member never sees an empty screen.
  hasScreeningQuestions: boolean
}
export type BookingFlowContext = {
  alreadySignedConsent: boolean
  program: ScreeningProgram
  appointmentType: ScreeningAppointmentType
  hasScreeningQuestions: boolean
  // The exclusion message id the not-eligible page renders. Seeded from the known-data gate at init
  // and overwritten by a screening DISQUALIFIED; null until/unless the member is turned away.
  exclusionMessageId: string | null
  // True only when a screening answer disqualified the member (not the known-data gate). Gates the
  // "back to questions" affordance so a known-data refusal can't be re-answered to bypass the gate.
  disqualifiedByScreener: boolean
  preferences: BookingPreferences | null
  selectedSlot: SelectedSlot | null
  // In-memory attempt identity: retries and slot changes reuse it; a new offering or actor does not.
  bookingSessionId: string | null
  bookingSessionOfferingId: string | null
  payment: BookingPayment | null
  paymentMethod: BookingPaymentMethod | null
  // The appointment returned by the booking call, shown on the confirmation screen.
  appointment: BookedAppointment | null
}

export type BookingFlowEvent =
  | { type: "CONTINUE" }
  | { type: "PREPARE_PAYMENT"; offeringId: string }
  | { type: "CONTINUE_TO_PAYMENT"; preferences: BookingPreferences; slot: SelectedSlot; payment: BookingPayment }
  | { type: "BACK_TO_AVAILABILITY" }
  // `fromScreener` distinguishes a screening-answer refusal (true → "back to questions" is offered,
  // re-answering makes sense) from a booking-time known-data refusal (false → terminal; re-answering
  // the screener can't change a known-data verdict, and it must not reopen the gate).
  | { type: "DISQUALIFIED"; exclusionMessageId: string; fromScreener: boolean }
  | { type: "BACK_TO_QUESTIONS" }
  | { type: "NEEDS_CONSENT" }
  // The booking call has already succeeded by the time this fires (the preferences screen owns
  // it), so the appointment rides along for the confirmation screen.
  | { type: "CONFIRM_BOOKING"; preferences: BookingPreferences; slot: SelectedSlot; appointment: BookedAppointment }

export const bookingFlowMachine = setup({
  types: {
    input: {} as BookingFlowInput,
    context: {} as BookingFlowContext,
    events: {} as BookingFlowEvent
  },
  guards: {
    isIneligible: ({ context }) => context.exclusionMessageId != null,
    // Skip the consent page when the member has already signed the current required
    // consents — fed in as machine input from getConsentStatus.
    alreadyConsented: ({ context }) => context.alreadySignedConsent,
    hasScreeningQuestions: ({ context }) => context.hasScreeningQuestions,
    disqualifiedByScreener: ({ context }) => context.disqualifiedByScreener
  },
  actions: {
    // Record the disqualification's message and its source for the not-eligible page. Only a screener
    // refusal sets disqualifiedByScreener → gating whether "back to questions" is offered (a
    // booking-time known-data refusal must not reopen the gate).
    recordExclusion: assign({
      exclusionMessageId: ({ event }) => (event.type === "DISQUALIFIED" ? event.exclusionMessageId : null),
      disqualifiedByScreener: ({ event }) => (event.type === "DISQUALIFIED" ? event.fromScreener : false)
    }),
    preparePayment: assign(({ context, event }) => {
      if (event.type !== "PREPARE_PAYMENT") return {}
      return {
        bookingSessionId:
          context.bookingSessionOfferingId === event.offeringId && context.bookingSessionId
            ? context.bookingSessionId
            : crypto.randomUUID(),
        bookingSessionOfferingId: event.offeringId,
        payment: null
      }
    }),
    recordPaymentSelection: assign({
      payment: ({ event }) => (event.type === "CONTINUE_TO_PAYMENT" ? event.payment : null),
      paymentMethod: "self_pay" as const,
      preferences: ({ event }) => (event.type === "CONTINUE_TO_PAYMENT" ? event.preferences : null),
      selectedSlot: ({ event }) => (event.type === "CONTINUE_TO_PAYMENT" ? event.slot : null)
    }),
    clearPaymentSelection: assign({ preferences: null, selectedSlot: null, paymentMethod: null, payment: null }),
    recordBooking: assign({
      preferences: ({ event }) => (event.type === "CONFIRM_BOOKING" ? event.preferences : null),
      selectedSlot: ({ event }) => (event.type === "CONFIRM_BOOKING" ? event.slot : null),
      appointment: ({ event }) => (event.type === "CONFIRM_BOOKING" ? event.appointment : null)
    }),
    // Clear the skip flag so the consent state doesn't bounce a member who started signed past it.
    markConsentUnsigned: assign({ alreadySignedConsent: false })
  }
}).createMachine({
  id: "bookingFlow",
  // The known-data eligibility gate runs first (MEM-760): an ineligible member never reaches consent.
  initial: "eligibilityGate",
  context: ({ input }) => ({
    alreadySignedConsent: input.alreadySignedConsent,
    program: input.program,
    appointmentType: input.appointmentType,
    // Seed from the known-data gate: non-null here means the eligibilityGate routes to notEligible.
    exclusionMessageId: input.knownDataExclusionMessageId,
    hasScreeningQuestions: input.hasScreeningQuestions,
    disqualifiedByScreener: false,
    preferences: null,
    selectedSlot: null,
    bookingSessionId: null,
    bookingSessionOfferingId: null,
    payment: null,
    paymentMethod: null,
    appointment: null
  }),
  states: {
    eligibilityGate: {
      always: [{ target: "notEligible", guard: "isIneligible" }, { target: "consent" }]
    },

    consent: {
      id: "consent",
      always: [{ target: "postConsent", guard: "alreadyConsented" }],
      on: { CONTINUE: "postConsent" }
    },

    // Transient router (no view): once consent is settled, go to the health-questions step only if
    // this offering has any — otherwise skip straight to availability so the member never sees an
    // empty screen (a follow-up has no gating questions).
    postConsent: {
      always: [{ target: "healthQuestions", guard: "hasScreeningQuestions" }, { target: "seeAvailability" }]
    },

    // Nested so it can grow into multiple (possibly member-data-driven) views without touching
    // the outer states. The page sends DISQUALIFIED on an ineligible result, else CONTINUE.
    healthQuestions: {
      id: "healthQuestions",
      initial: "view",
      states: {
        view: {
          on: {
            CONTINUE: { target: "done" },
            DISQUALIFIED: { target: "#notEligible", actions: "recordExclusion" }
          }
        },
        done: { type: "final" }
      },
      onDone: { target: "seeAvailability" }
    },

    // Nested so the availability journey can grow without touching the outer states. The
    // preferences view now also hosts the date + time picker (MEM-716): the member sets
    // visit type + location, picks a slot from availability, and confirms — recording the
    // preferences and chosen slot. Empty availability is handled inline on that screen (a
    // no-slots tile), so there's no terminal no-availability state.
    seeAvailability: {
      initial: "preferences",
      states: {
        preferences: {
          on: {
            PREPARE_PAYMENT: { actions: "preparePayment" },
            CONTINUE_TO_PAYMENT: { target: "#payment", actions: "recordPaymentSelection" },
            CONFIRM_BOOKING: { target: "done", actions: "recordBooking" },
            // Back to the questions step (MEM-825). Guarded on hasScreeningQuestions so it's a no-op
            // when there were none — the preferences screen backs out of the flow itself in that case.
            BACK_TO_QUESTIONS: { target: "#healthQuestions", guard: "hasScreeningQuestions" },
            // Booking-time eligibility rejection (MEM-771): terminal, routed out like a screening disqualification.
            DISQUALIFIED: { target: "#notEligible", actions: "recordExclusion" },
            // Booking blocked on unsigned consent: back to consent (member re-selects a slot afterward).
            NEEDS_CONSENT: { target: "#consent", actions: "markConsentUnsigned" }
          }
        },
        done: { type: "final" }
      },
      onDone: { target: "booked" }
    },

    // The flag-gated test bypass books here until payment authorization is implemented.
    payment: {
      id: "payment",
      on: {
        BACK_TO_AVAILABILITY: { target: "seeAvailability", actions: "clearPaymentSelection" },
        CONFIRM_BOOKING: { target: "booked", actions: "recordBooking" },
        DISQUALIFIED: { target: "notEligible", actions: "recordExclusion" },
        NEEDS_CONSENT: { target: "consent", actions: ["markConsentUnsigned", "clearPaymentSelection"] }
      }
    },
    booked: { type: "final" },
    // Not final: a screener-disqualified member can go back to re-answer (guarded, so a known-data
    // refusal — which never sets disqualifiedByScreener — can't use this to bypass the gate).
    notEligible: {
      id: "notEligible",
      on: { BACK_TO_QUESTIONS: { target: "healthQuestions", guard: "disqualifiedByScreener" } }
    }
  }
})
